import {
    ChartElementType,
    SelectStyleType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import flatten from 'lodash/flatten';
import { BaseChartElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/base';
import type {
    IChartElementGroup,
    IChartElement,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import type { G2Component } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/types';
import { ComponentOption } from '@tencent/chart/lib/antv/g2/interface';
import type { Chart as G2Chart } from '@tencent/chart/lib/antv/g2';

// grid也属于axisElement，其direction为none。
export type AxisElementDirection = 'left' | 'bottom' | 'top' | 'right' | 'none';
export interface IAxisElement extends IChartElement {
    extra: { dim: string; }
    direction: AxisElementDirection;
}

export class Axis extends BaseChartElement {
    public getElements(chart: G2Chart): Array<IChartElement | IChartElementGroup> {
        const g2AxisControllers = chart.views.map(view => view.controllers.find(controller => controller.name === 'axis'));
        // @ts-ignore
        const g2AxisComponents = flatten(g2AxisControllers
            .map(controller => controller?.getComponents())) as ComponentOption[];

        const axisElements = this.getAxisElements(g2AxisComponents);
        const gridElements = this.getGridElements(g2AxisComponents);


        // axis的层级比grid高，所以放后面
        return [...gridElements, ...axisElements];
    }

    private getAxisElements(components: ComponentOption[]): Array<IAxisElement | IChartElementGroup<IAxisElement>> {
        const g2AxisComponents = components.filter(comp => comp.type === 'axis');

        const axisElements = g2AxisComponents?.map((
            g2Comp
            , index,
        ): Array<IAxisElement | IChartElementGroup<IAxisElement>> => {
            const axisGroup = g2Comp.component?.cfg?.group as G2Component;
            const childrenList = axisGroup.cfg?.children;
            const { direction, extra: g2ExtraProp } = g2Comp;

            const extraInfo = {
                ...g2ExtraProp,
                axisId: g2Comp.component?.cfg?.id,  // 用于关联model的axis数据
            };

            if (childrenList) {
                const childrenElements: Array<IAxisElement | IChartElementGroup<IAxisElement>> = [];
                // 坐标轴分为三部分，1. 坐标轴线 + 坐标轴文本 2.坐标轴tick 3.坐标轴标题
                const axisLine = childrenList.find(el => el.cfg.name === 'axis-line');
                const axisLabelGroup = childrenList.find(el => el.cfg.name === 'axis-label-group');
                // 暂时先不处理tickline的交互
                const axisTicklineGroup = childrenList.find(el => el.cfg.name === 'axis-tickline-group');
                const axisTitle = childrenList.find(el => el.cfg.name === 'axis-title');

                // 如果没有label，在交互上无法选中label + line那个元素的
                if (axisLabelGroup) {
                    if (this.isPolarisCoordinate(g2Comp)) {
                        const groupId = `${ChartElementType.AXIS_LABEL}-${direction}-group-${index}`;
                        // TODO 这里返回 axis label 的 group
                        const labelElements: IAxisElement[] | undefined = axisLabelGroup.cfg
                            .children?.map((ele, eleIndex) => {
                                const axisLabelBBox = ele.getCanvasBBox?.() || ele.getBBox();
                                const rect = this.convertBBoxToRect(axisLabelBBox);
                                const path = this.convertRectToPath(rect);

                                return {
                                    rect,
                                    path,
                                    direction: direction as AxisElementDirection,
                                    type: ChartElementType.AXIS_LABEL,
                                    id: `${ChartElementType.AXIS_LABEL}-${direction}-${eleIndex}`,
                                    extra: extraInfo,
                                    groupId,
                                };
                            });

                        if (labelElements) {
                            const labelGroup: IChartElementGroup<IAxisElement> = {
                                group: labelElements,
                                id: groupId,
                                type: ChartElementType.AXIS_LABEL,
                            };

                            childrenElements.push(labelGroup);
                        }
                    } else {
                        // @ts-ignore
                        const axisLabelBBox = axisLabelGroup.getCanvasBBox?.() || axisLabelGroup.getBBox();
                        const rect = this.convertBBoxToRect(axisLabelBBox);
                        const path = this.convertRectToPath(rect);

                        const axisLabelElement = {
                            rect,
                            path,
                            direction: direction as AxisElementDirection,
                            type: ChartElementType.AXIS_LABEL,
                            id: `${ChartElementType.AXIS_LABEL}-${direction}-${index}`,
                            extra: extraInfo,
                        };

                        if (axisLine) {
                            const axisLineBBox = axisLine.getBBox();

                            const top = Math.min(axisLabelBBox.minY, axisLineBBox.minY);
                            const left = Math.min(axisLabelBBox.minX, axisLineBBox.minX);
                            axisLabelElement.rect = {
                                top,
                                left,
                                width: Math.max(axisLabelBBox.maxX, axisLineBBox.maxX) - left,
                                height: Math.max(axisLabelBBox.maxY, axisLineBBox.maxY) - top,
                            };
                            axisLabelElement.path = this.convertRectToPath(axisLabelElement.rect);
                        }

                        childrenElements.push(axisLabelElement);
                    }
                }

                if (axisTitle) {
                    const rect = this.convertBBoxToRect(axisTitle.getCanvasBBox?.() || axisTitle.getBBox?.());
                    const path = this.convertRectToPath(rect);
                    childrenElements.push({
                        // 这里使用矩阵变换后的bbox
                        rect,
                        path,
                        direction: direction as AxisElementDirection,
                        type: ChartElementType.AXIS_TITLE,
                        id: `${ChartElementType.AXIS_TITLE}-${direction}-${index}`,
                        extra: extraInfo,
                    });
                }

                return childrenElements;
            }

            const bbox = g2Comp.component.getBBox();
            const rect = {
                height: bbox.height,
                width: bbox.width,
                left: bbox.x,
                top: bbox.y,
            };
            const path = this.convertRectToPath(rect);

            return [{
                rect,
                path,
                extra: extraInfo,
                direction: direction as AxisElementDirection,
                type: ChartElementType.AXIS_LABEL,
                id: `${ChartElementType.AXIS_LABEL}-${direction}`,
            }];
        });

        // 存在多个坐标轴，这里把多个坐标轴里的elements打平
        return flatten(axisElements);
    }

    private getGridElements(components: ComponentOption[]): Array<IChartElementGroup<IAxisElement>> {
        // 组合图里会有多个grid
        const g2GridComponents = components.filter(comp => comp.type === 'grid').filter(c => !!c);
        const gridGroupList: Array<IChartElementGroup<IAxisElement>> = [];

        g2GridComponents.forEach((g2GridComponent, gridComponentIndex) => {
            const gridItems = g2GridComponent?.component?.cfg?.items as { points: any[], type: string }[];

            const majorGridElements: IAxisElement[] = [];
            const minorGridElements: IAxisElement[] = [];

            (g2GridComponent?.component?.cfg?.group as G2Component)
                ?.cfg?.children?.forEach((child, index) => {
                    const { canvasBBox, attrs, bbox } = child.cfg;
                    const rect = this.convertBBoxToRect(canvasBBox || bbox);
                    const gridElementType = gridItems?.[index].type;
                    const isMinorGrid = gridElementType === 'subGrid';
                    const gridType =  isMinorGrid ? ChartElementType.MINOR_GRID : ChartElementType.MAJOR_GRID;


                    const ele = {
                        rect,
                        path: attrs?.path || [],
                        extra: {
                            ...g2GridComponent?.extra,
                            axisId: g2GridComponent?.extra?.axis?.id,
                        },
                        type: gridType,
                        id: `${gridType}-${gridComponentIndex}-${index}`,
                        direction: g2GridComponent?.direction as AxisElementDirection,
                        shapeType: 'line',
                        selectStyle: SelectStyleType.ANCHOR,
                        disableSingleSelect: true,
                    };

                    if (isMinorGrid) {
                        minorGridElements.push(ele);
                    } else {
                        majorGridElements.push(ele);
                    }
                });


            [majorGridElements, minorGridElements].map((gridElements) => {
                if (gridElements.length > 0) {
                    const gridType = gridElements[0].type;
                    const groupId = `${gridType}-group${gridComponentIndex}`;

                    const gridLineElementGroup: IChartElementGroup<IAxisElement> = {
                        group: gridElements.map(item => ({
                            ...item,
                            groupId,
                        })),
                        type: gridType,
                        id: groupId,
                    };

                    gridGroupList.push(gridLineElementGroup);
                }
            });
        });

        return gridGroupList;
    }

    private isPolarisCoordinate(comp: ComponentOption) {
        return comp.component.cfg.type === 'circle';
    }
}
